/**
 * PlatformConsole — surfaced only to Platform Support Admins.
 * Shows tenant list, root-admin list, system audit log, and a recovery ping.
 *
 * This is intentionally NOT the tenant dashboard. It's a separate surface.
 */
import React, { useEffect, useState } from 'react';
import AppHeader from './layout/AppHeader';
import RateLimitsPanel from './RateLimitsPanel';
import AuditLogViewer from './AuditLogViewer';
import SalesDemoProvisioningPanel from './SalesDemoProvisioningPanel';
import { MfaPolicyPanel, RetentionPanel, SSOPanel, ResidencyPanel } from './GovernancePanels';
import { useAuth } from '../context/AuthContext';
import { usePlatform } from '../context/PlatformContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function jsonGet(path) {
  const res = await fetch(`${API_URL}${path}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

export default function PlatformConsole() {
  const { user, logout } = useAuth();
  const { activeTenantId, setActiveTenant, clearActiveTenant } = usePlatform();
  const [tenants, setTenants] = useState(null);
  const [rootAdmins, setRootAdmins] = useState(null);
  const [recovery, setRecovery] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, r] = await Promise.all([
        jsonGet('/api/platform/tenants'),
        jsonGet('/api/platform/root-admins'),
      ]);
      setTenants(t);
      setRootAdmins(r);
    } catch (e) {
      setError(e?.message || 'Failed to load platform data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const runRecoveryPing = async () => {
    try {
      const res = await fetch(`${API_URL}/api/platform/recovery/ping`, { method: 'POST' });
      const data = await res.json();
      setRecovery(data);
    } catch (e) {
      setRecovery({ status: 'error', error: e?.message });
    }
  };

  return (
    <>
      <AppHeader active="platform" />
      <div className="console-container" data-testid="platform-console">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span className="version" data-testid="platform-badge">Platform Console</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {user?.role_label} — {user?.email}
          </span>
          <button className="btn btn-outline btn-sm" onClick={loadAll} data-testid="platform-refresh">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="panel" style={{ marginBottom: 12, borderColor: 'var(--danger)' }}>
          <div className="panel-body" style={{ color: 'var(--danger)' }}>{error}</div>
        </div>
      )}

      {loading && !tenants && (
        <div className="panel"><div className="panel-body">Loading platform state…</div></div>
      )}

      {/* Tenants */}
      <div className="panel" style={{ marginBottom: 12 }} data-testid="platform-tenants-panel">
        <div className="panel-header">Tenants</div>
        <div className="panel-body" style={{ padding: 0 }}>
          <div className="data-table-wrap">
            <table className="data-table" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Plan</th>
                  <th>Status</th>
                  <th>Users</th>
                  <th>Companies</th>
                  <th>Created</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tenants?.tenants?.map((t) => {
                  const isActive = activeTenantId === t.id;
                  const status = t.activation_status || '-';
                  const isInactive = status && status !== 'active';
                  return (
                  <tr key={t.id} data-testid={`tenant-row-${t.slug}`}>
                    <td style={{ fontWeight: 600 }}>{t.name}</td>
                    <td><span className="badge badge-type">{t.slug}</span></td>
                    <td data-testid={`tenant-plan-${t.slug}`}>
                      <span className="badge badge-type" style={{ textTransform: 'capitalize' }}>
                        {t.plan_id || '-'}
                      </span>
                    </td>
                    <td data-testid={`tenant-status-${t.slug}`}>
                      <span
                        className="badge"
                        style={{
                          textTransform: 'capitalize',
                          color: isInactive ? 'var(--danger)' : 'var(--success, #137333)',
                          borderColor: 'currentColor',
                        }}
                      >
                        {status}
                      </span>
                    </td>
                    <td style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{t.user_count}</td>
                    <td
                      data-testid={`tenant-company-count-${t.slug}`}
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      {typeof t.company_count === 'number' ? t.company_count : '-'}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {isActive ? (
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          data-testid={`platform-act-as-clear-${t.slug}`}
                          onClick={clearActiveTenant}
                          title="Stop acting as this tenant"
                        >
                          Exit context
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          data-testid={`platform-act-as-${t.slug}`}
                          onClick={() => setActiveTenant({ id: t.id, name: t.name })}
                          title="Act as this tenant for the rest of the session"
                        >
                          Act as
                        </button>
                      )}
                    </td>
                  </tr>
                );
                })}
                {tenants && tenants.count === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 16, color: 'var(--text-secondary)' }}>
                    No tenants configured
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <SalesDemoProvisioningPanel onProvisioned={loadAll} />

      {/* Platform Support Admins */}
      <div className="panel" style={{ marginBottom: 12 }} data-testid="platform-root-admins-panel">
        <div className="panel-header">Platform Support Admins</div>
        <div className="panel-body" style={{ padding: 0 }}>
          <div className="data-table-wrap">
            <table className="data-table" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {rootAdmins?.admins?.map((a) => (
                  <tr key={a.id} data-testid={`root-admin-row-${a.id}`}>
                    <td style={{ fontWeight: 600 }}>{a.email}</td>
                    <td>{a.name}</td>
                    <td style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      {a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Recovery */}
      <div className="panel" style={{ marginBottom: 12 }} data-testid="platform-recovery-panel">
        <div className="panel-header">Recovery</div>
        <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={runRecoveryPing} data-testid="recovery-ping-btn" style={{ alignSelf: 'flex-start' }}>
            Run recovery ping
          </button>
          {recovery && (
            <pre data-testid="recovery-ping-output" style={{ fontSize: 11, background: 'var(--bg)', padding: 8, borderRadius: 4, margin: 0 }}>
              {JSON.stringify(recovery, null, 2)}
            </pre>
          )}
        </div>
      </div>

      {/* Rate limits (Platform Root only) */}
      <RateLimitsPanel />

      {/* Governance */}
      <MfaPolicyPanel />
      <RetentionPanel />
      <SSOPanel />
      <ResidencyPanel />

      {/* Audit log */}
      <AuditLogViewer />

      <div className="app-powered-footer"><span className="app-powered-footer-copy">© 2026 QUOD-Tech Ltd. All rights reserved.</span> <span className="app-powered-footer-sep" aria-hidden="true">·</span> <span className="app-powered-footer-mark">POWERED BY QUOD-TECH</span></div>
    </div>
    </>
  );
}
